import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Volume2, VolumeX, PartyPopper } from "lucide-react";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import {
  isConfettiMuted,
  setConfettiMuted,
  playConfettiChime,
} from "@/lib/confettiSound";

const WALL_SEEN_KEY = "bailey_wall_last_seen";

// Fire a subtle confetti burst anchored on the Wall of Families pill.
// Two staggered bursts angled inward from left/right for warmth.
function celebrateFreshFamily(anchorEl) {
  const rect = anchorEl?.getBoundingClientRect();
  const originX = rect ? (rect.left + rect.width / 2) / window.innerWidth : 0.3;
  const originY = rect ? (rect.top + rect.height / 2) / window.innerHeight : 0.5;
  const colors = ["#1a2947", "#c9a94d", "#8a7a3a", "#f5efe0", "#5f8a52"];
  const shared = { spread: 60, ticks: 200, gravity: 0.9, scalar: 0.9, colors };
  confetti({
    ...shared,
    particleCount: 45,
    angle: 70,
    origin: { x: Math.max(0.05, originX - 0.1), y: Math.min(0.9, originY + 0.05) },
  });
  confetti({
    ...shared,
    particleCount: 45,
    angle: 110,
    origin: { x: Math.min(0.95, originX + 0.1), y: Math.min(0.9, originY + 0.05) },
  });
}

// Social-proof strip on the Landing page: shows the last names of paid
// households as little pills. Pulls from /api/stats/public which sorts
// newest-paid-first so the freshest household leads.
export default function WallOfFamilies() {
  const [families, setFamilies] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [muted, setMuted] = useState(() => isConfettiMuted());
  const wallRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/stats/public")
      .then((r) => {
        if (cancelled) return;
        const list =
          r.data?.paid_families ||
          (r.data?.paid_last_names || []).map((n) => ({
            last_name: n,
            latest_paid_at: null,
          }));
        setFamilies(list);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // Confetti when the visitor lands after a NEW paid family has joined
  // (newest paid within the last 24h AND newer than the visitor's stored
  // last-seen marker). Respects prefers-reduced-motion.
  useEffect(() => {
    if (!loaded || families.length === 0) return;
    const newest = families[0];
    if (!newest?.latest_paid_at) return;
    const newestMs = new Date(newest.latest_paid_at).getTime();
    if (Number.isNaN(newestMs)) return;
    if (Date.now() - newestMs > 24 * 60 * 60 * 1000) return;

    let lastSeen = 0;
    try {
      lastSeen = parseInt(localStorage.getItem(WALL_SEEN_KEY) || "0", 10) || 0;
    } catch {}
    if (newestMs <= lastSeen) return; // already celebrated

    const prefersReduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      try {
        localStorage.setItem(WALL_SEEN_KEY, String(newestMs));
      } catch {}
      return;
    }

    const t = setTimeout(() => {
      celebrateFreshFamily(wallRef.current);
      playConfettiChime();
      try {
        localStorage.setItem(WALL_SEEN_KEY, String(newestMs));
      } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [loaded, families]);

  if (!loaded || families.length === 0) return null;

  // Any paid family whose latest_paid_at is within the last 7 days shows
  // up in the "New this week" ribbon so returning visitors who missed the
  // confetti still notice fresh momentum on the Wall.
  const now = Date.now();
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  const newThisWeek = families.filter((f) => {
    if (!f.latest_paid_at) return false;
    const t = new Date(f.latest_paid_at).getTime();
    return !Number.isNaN(t) && now - t <= weekMs;
  });

  const ribbonText =
    newThisWeek.length === 0
      ? null
      : newThisWeek.length === 1
      ? `The ${newThisWeek[0].last_name} family joined this week`
      : newThisWeek.length === 2
      ? `The ${newThisWeek[0].last_name} & ${newThisWeek[1].last_name} families joined this week`
      : `${newThisWeek.length} new families joined this week`;

  return (
    <div ref={wallRef} className="mt-8 max-w-2xl" data-testid="wall-of-families">
      {ribbonText && (
        <div
          data-testid="wall-weekly-ribbon"
          className="mb-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 pl-2 pr-3 py-1"
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
            <PartyPopper className="h-3 w-3" />
            New this week
          </span>
          <span className="text-xs font-medium text-emerald-800">
            {ribbonText}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase font-semibold text-[#8a7a3a]">
          <Sparkles className="h-3.5 w-3.5" />
          Families already in for the reunion
        </div>
        <button
          type="button"
          onClick={() => {
            const next = !muted;
            setMuted(next);
            setConfettiMuted(next);
            if (!next) playConfettiChime();
          }}
          title={muted ? "Enable celebration sound" : "Mute celebration sound"}
          data-testid="wall-mute-toggle"
          className="inline-flex items-center gap-1 text-[10px] font-medium text-stone-500 hover:text-[#1a2947] transition-colors"
        >
          {muted ? (
            <>
              <VolumeX className="h-3.5 w-3.5" />
              Sound off
            </>
          ) : (
            <>
              <Volume2 className="h-3.5 w-3.5" />
              Sound on
            </>
          )}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {families.map((f, idx) => (
          <FamilyPill key={f.last_name} family={f} isNewest={idx === 0} />
        ))}
      </div>
      <div className="mt-2 text-[11px] text-stone-500">
        {families.length}{" "}
        {families.length === 1 ? "household" : "households"} paid in — join
        them.
      </div>
    </div>
  );
}

// A paid family becomes "newest" for 24h after their last payment. The
// pill gets a sparkle + emerald ring so returning visitors notice the
// fresh momentum on the Wall.
function FamilyPill({ family, isNewest }) {
  const { last_name, latest_paid_at } = family;
  const now = Date.now();
  const within24h =
    latest_paid_at &&
    !Number.isNaN(new Date(latest_paid_at).getTime()) &&
    now - new Date(latest_paid_at).getTime() <= 24 * 60 * 60 * 1000;
  const isFresh = isNewest && within24h;

  const baseClass =
    "inline-flex items-center gap-1 rounded-full text-xs font-medium px-3 py-1 shadow-[0_1px_0_rgba(26,41,71,0.04)] transition-colors";
  const idleClass = "bg-white/90 border border-[#e6e2d6] text-stone-800 hover:border-[#1a2947]/40";
  const freshClass =
    "bg-emerald-50 border border-emerald-300 text-emerald-800 ring-1 ring-emerald-200/70";

  return (
    <span
      data-testid={`wall-pill-${last_name.toLowerCase()}`}
      data-fresh={isFresh ? "true" : "false"}
      className={`${baseClass} ${isFresh ? freshClass : idleClass}`}
      title={
        latest_paid_at
          ? `Paid ${new Date(latest_paid_at).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}`
          : undefined
      }
    >
      {isFresh && <Sparkles className="h-3 w-3 text-emerald-600" />}
      {last_name}
      {isFresh && (
        <span className="ml-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5">
          Just paid
        </span>
      )}
    </span>
  );
}
